import { useEffect, useLayoutEffect, useState } from "react";
import { Head } from "vite-react-ssg";
import { FaWhatsapp, FaTelegramPlane } from "react-icons/fa";
import mascote1 from "@/assets/mascote/mascote-1.webp";
import mascote2 from "@/assets/mascote/mascote-2.webp";
import mascote3 from "@/assets/mascote/mascote-3.webp";
import mascote4 from "@/assets/mascote/mascote-4.webp";
import { ActionButton } from "@/components/ui/action-button";
import { GlitchText } from "@/components/fx/GlitchText";
import { SeoHead } from "@/components/seo/SeoHead";
import { trackLeadAndGo, generateEventId } from "@/lib/facebookPixel";
import { trackCtaClick, buildOutboundUrl } from "@/lib/analytics";
import { SITE_CONFIG } from "@/config/site";
import { memberStats, formatMemberCount } from "@/data/stats";
import { CTA_COPY } from "@/lib/cta-copy";
import type { CtaType } from "@/types";
import { aplicarMascoteDoDia } from "@/lib/mascote-do-dia";
import "@/styles/home-radar.css";

/**
 * Home — "Radar de Bugs" (redesign aprovado pelo operador em 2026-10-20,
 * depois de rodar como prévia em /nova-home).
 *
 * O slogan é "caçamos bugs de preço 24h por dia", então a peça-assinatura é um
 * radar girando com o mascote no centro. Quando o feixe passa, acendem
 * etiquetas de preço. O resto é papel creme com grão e um "cupom fiscal da
 * economia" no lugar de lista de benefícios.
 *
 * FREEZE (CLAUDE.md), com aprovação do operador em 2026-10-20:
 *   - mascote TROCADO pelo ícone novo (chapéu + camisa havaiana, sem o texto
 *     "Macaco das Promos" em volta — "fica mais clean");
 *   - paleta TROCADA para a do ícone (turquesa, amarelo, laranja, marrom). O
 *     vermelho #d32f2f → #7b1f1f saiu da home; segue nas outras páginas;
 *   - H1 "Macaco das Promos", URLs e as animações dos CTAs (cta-breathe-*,
 *     pulse-*) continuam idênticos — os botões são os mesmos ActionButton.
 *
 * O que a home anterior tinha e esta não tem (a prévia aprovada não tinha):
 * faixa rolante (PromoMarquee), BannerTopo, sticky bar do celular e o A/B de
 * copy dos CTAs (cta-copy-benefit-2026-q3) — um teste de copy não continua
 * válido atravessando um redesign inteiro. Os overlays globais (exit intent,
 * push, PWA) seguem montados pelo App.tsx, como antes.
 *
 * Medição: as MESMAS tags da home anterior (cta_location "hero", Lead
 * location "Hero", utm_campaign "cta_hero"), para o histórico de relatórios
 * continuar comparável antes/depois da troca.
 */

/**
 * Etiquetas no anel do radar. `ang` em graus, horário, a partir do topo; `k` é
 * a distância do centro da etiqueta até a BORDA do radar, em em do corpo da
 * etiqueta (ver --etq em home-radar.css).
 *
 * Reposicionadas em 2026-10-23 para o mascote crescer: ângulos e distâncias
 * saíram de uma busca que manteve cada etiqueta a 7px do mascote, a 6px da
 * borda, longe do crachá "Caçando agora" (embaixo) e das vizinhas, em radares
 * de 259 a 420px. As largas ficam mais perto do eixo vertical, onde ocupam menos
 * espaço na direção do centro.
 */
const ETIQUETAS = [
  { texto: "−73%", ang: 34, k: 2.833, tom: "amarelo" },
  { texto: "BUG", ang: 94, k: 2.558, tom: "laranja" },
  { texto: "CUPOM", ang: 143, k: 3.075, tom: "papel" },
  { texto: "R$ 9,90", ang: 202, k: 2.783, tom: "amarelo" },
  { texto: "FRETE 0", ang: 324, k: 3.242, tom: "agua" },
] as const;

/**
 * As quatro versões do mascote pintado (2026-10-23), em rodízio a cada 2 dias
 * (ver src/lib/mascote-do-dia.ts). A ordem é a do calendário.
 */
const MASCOTES = [
  { src: mascote1, alt: "Macaco das Promos, o mascote, de chapéu de explorador e camisa havaiana, olhando por cima do ombro com o celular na mão" },
  { src: mascote2, alt: "Macaco das Promos, o mascote, apontando para o celular que mostra uma etiqueta de desconto" },
  { src: mascote3, alt: "Macaco das Promos, o mascote, piscando e fazendo joinha com o celular na mão" },
  { src: mascote4, alt: "Macaco das Promos, o mascote, de frente, com sorriso de canto e o celular na mão" },
] as const;

/**
 * Escolhe a versão do dia ANTES do <body> existir (sem a imagem trocar depois
 * de aparecer). Só no HTML estático — o <Head> quebra no cliente (ver SeoHead);
 * quem chega na home navegando dentro do site é coberto pela própria página.
 */
function ScriptDoMascote() {
  if (!import.meta.env.SSR) return null;
  const urls = JSON.stringify(MASCOTES.map((m) => m.src));
  return (
    <Head>
      <script>{`(${aplicarMascoteDoDia.toString()})(document.documentElement,new Date(),${urls})`}</script>
    </Head>
  );
}

// useLayoutEffect só existe no navegador; no build estático vira useEffect
// (que o servidor ignora) para não sujar o log com o aviso do React.
const useLayoutEffectNoCliente = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** Uma volta do feixe. As etiquetas acendem sincronizadas com ele. */
const VOLTA_S = 6;

const LINHAS_DO_CUPOM = [
  { item: "Entrar no grupo", valor: "R$ 0,00" },
  { item: "Promos caçadas por dia", valor: "+50" },
  { item: "Caça de bugs de preço", valor: "24h" },
  { item: "Taxa pra sair", valor: "R$ 0,00" },
] as const;

const Index = () => {
  const [abrindo, setAbrindo] = useState<CtaType | null>(null);

  // Quem chega na home navegando dentro do site não carregou o HTML da home,
  // então o script do <head> não rodou. Antes da pintura, para não aparecer a
  // versão 1 e trocar em seguida.
  useLayoutEffectNoCliente(() => {
    if (!document.documentElement.hasAttribute("data-mascote")) {
      aplicarMascoteDoDia(document.documentElement, new Date(), MASCOTES.map((m) => m.src));
    }
  }, []);

  const entrar = async (tipo: CtaType) => {
    if (abrindo) return;
    const base = tipo === "whatsapp" ? SITE_CONFIG.whatsappUrl : SITE_CONFIG.telegramUrl;
    const url = buildOutboundUrl(base, {
      utm_source: "primata",
      utm_medium: "landing",
      utm_campaign: "cta_hero",
    });
    const eventId = generateEventId();
    setAbrindo(tipo);
    trackCtaClick(tipo, "hero", memberStats[tipo], eventId);
    await trackLeadAndGo(url, tipo, { location: "Hero", eventId });
    setAbrindo(null);
  };

  return (
    <div className="nh">
      <SeoHead path="/" themeColor="#e9f7f6" htmlClass="page-radar" />
      <ScriptDoMascote />

      {/* Papel creme com grão + brilho rosado atrás do radar. Camada fixa. */}
      <div className="nh-papel" aria-hidden="true" />

      <main className="nh-main">
        <section className="nh-hero">
          {/* ---- Radar ---- */}
          <div className="nh-radar-wrap">
            <div
              className="nh-radar"
              style={{ ["--volta" as string]: `${VOLTA_S}s` }}
              aria-hidden="true"
            >
              {/* Tela redonda com overflow:hidden. O feixe é um quadrado girando:
                  a 45° a diagonal dele passa da borda da tela do celular e criava
                  rolagem lateral intermitente (a página "balançava" no iPhone). */}
              <span className="nh-radar-tela">
                <span className="nh-radar-disco" />
                <span className="nh-radar-grade" />
                <span className="nh-radar-aneis" />
                <span className="nh-radar-feixe" />
              </span>

              {ETIQUETAS.map((e) => (
                <span
                  key={e.texto}
                  className="nh-blip"
                  style={{ ["--ang" as string]: `${e.ang}deg`, ["--k" as string]: e.k }}
                >
                  <span
                    className={`nh-etiqueta nh-etiqueta--${e.tom}`}
                    // Acende quando o feixe passa: atraso negativo = já começa
                    // na fase certa da volta (ver keyframes nh-acende).
                    style={{ animationDelay: `${(e.ang / 360) * VOLTA_S - VOLTA_S}s` }}
                  >
                    {e.texto}
                  </span>
                </span>
              ))}
            </div>

            {/* Mascote fora do aria-hidden: é a imagem de marca e o LCP.
                As 4 versões estão no HTML; o CSS mostra só a do dia
                (html[data-mascote]) e as outras, escondidas + lazy, nem são
                baixadas. A janela redonda é a "boca" de onde ele sobe na
                entrada. */}
            <div className="nh-mascote">
              <span className="nh-mascote-janela">
                {MASCOTES.map((m, i) => (
                  // loading ANTES de src: o React grava os atributos na
                  // ordem das props, e um <img> que recebe src antes de
                  // loading="lazy" já começa a baixar (as 4 baixavam).
                  <img
                    key={m.src}
                    loading="lazy"
                    decoding="async"
                    data-v={i + 1}
                    src={m.src}
                    alt={m.alt}
                    width={512}
                    height={512}
                  />
                ))}
              </span>
            </div>

            <p className="nh-ao-vivo">
              <span className="nh-ao-vivo-ponto" aria-hidden="true" />
              Caçando agora
            </p>
          </div>

          {/* ---- Texto + CTAs ---- */}
          <div className="nh-copy">
            <p className="nh-prova nh-entra" style={{ animationDelay: "120ms" }}>
              {formatMemberCount(memberStats.totalAggregate)} caçadores nos grupos
            </p>

            {/* H1 FROZEN: "Macaco das Promos" */}
            <h1 className="nh-titulo">
              Macaco das <GlitchText text="Promos" className="nh-titulo-promos" />
            </h1>

            <p className="nh-sub nh-entra" style={{ animationDelay: "180ms" }}>
              Caçamos <span>cupons e bugs</span> de preço 24h por dia.
            </p>

            <div className="nh-ctas nh-entra" style={{ animationDelay: "240ms" }}>
              <ActionButton
                id="cta-whatsapp"
                aria-label="Entrar no grupo do WhatsApp"
                variant="secondary"
                className="w-full justify-start group btn-whatsapp"
                disabled={abrindo !== null}
                onClick={() => entrar("whatsapp")}
              >
                <span className="icon-bubble glow-whatsapp shrink-0 mr-2 sm:mr-3">
                  <FaWhatsapp size={24} />
                </span>
                {abrindo === "whatsapp" ? (
                  <span className="flex flex-1 text-left font-medium text-[15px] sm:text-base">
                    Abrindo WhatsApp...
                  </span>
                ) : (
                  <span className="flex flex-1 flex-col gap-y-0.5 text-left min-w-0">
                    <span className="font-medium-bold text-[15px] sm:text-base leading-tight">
                      {CTA_COPY.control.whatsapp}
                    </span>
                    <span className="text-[14px] sm:text-sm font-bold text-white">
                      {formatMemberCount(memberStats.whatsapp)} já entraram
                    </span>
                  </span>
                )}
              </ActionButton>

              <ActionButton
                id="cta-telegram"
                aria-label="Entrar no grupo do Telegram"
                className="w-full justify-start group btn-telegram"
                disabled={abrindo !== null}
                onClick={() => entrar("telegram")}
              >
                <span className="icon-bubble glow-telegram shrink-0 mr-2 sm:mr-3">
                  <FaTelegramPlane size={24} />
                </span>
                {abrindo === "telegram" ? (
                  <span className="flex flex-1 text-left font-medium text-[15px] sm:text-base">
                    Abrindo Telegram...
                  </span>
                ) : (
                  <span className="flex flex-1 flex-col gap-y-0.5 text-left min-w-0">
                    <span className="font-medium-bold text-[15px] sm:text-base leading-tight">
                      {CTA_COPY.control.telegram}
                    </span>
                    <span className="text-[14px] sm:text-sm font-bold text-white">
                      {formatMemberCount(memberStats.telegram)} membros
                    </span>
                  </span>
                )}
              </ActionButton>

              <p className="nh-garantia">✓ 100% grátis · ✓ Sem cartão · ✓ Saia quando quiser</p>
            </div>
          </div>
        </section>

        {/* ---- Cupom fiscal da economia ---- */}
        <section
          className="nh-cupom-wrap nh-entra"
          style={{ animationDelay: "320ms" }}
          aria-labelledby="cupom-titulo"
        >
          <div className="nh-cupom">
            <h2 id="cupom-titulo" className="nh-cupom-cabeca">
              <strong>Macaco das Promos</strong>
              <span>Cupom da economia · via do caçador</span>
            </h2>

            <dl className="nh-cupom-linhas">
              {LINHAS_DO_CUPOM.map((l) => (
                <div key={l.item} className="nh-cupom-linha">
                  <dt>{l.item}</dt>
                  <dd>{l.valor}</dd>
                </div>
              ))}
            </dl>

            <div className="nh-cupom-total">
              <span>Você paga</span>
              <strong>GRÁTIS</strong>
            </div>

            <div className="nh-cupom-barras" aria-hidden="true" />
            <p className="nh-cupom-rodape">obrigado por economizar com a gente 🐒</p>
          </div>
        </section>
      </main>

      <footer className="nh-rodape">
        <p>© 2026 Macaco das Promos • Todos os direitos reservados</p>
        <p>Somos afiliados das lojas — você não paga nada a mais.</p>
      </footer>
    </div>
  );
};

export default Index;
